import React from 'react';
import { Reveal } from './ui/Reveal';
import { User, Leaf, Sparkles, Heart, Award, GraduationCap } from 'lucide-react';
import { useSiteContent } from '../contexts/SiteContentContext';
export function AboutSection() {
  const {
    content
  } = useSiteContent();
  const about = content.about;
  return <section id="sobre-nosotros" className="py-16 md:py-24 px-4 sm:px-6 md:px-16" data-id="element-667">
      <div className="max-w-7xl mx-auto" data-id="element-668">
        <Reveal className="text-center mb-12 md:mb-16" data-id="element-669">
          <span className="inline-block bg-sage-light/30 text-earth text-[10px] md:text-xs font-semibold uppercase tracking-widest px-3 py-1 md:px-3.5 md:py-1.5 rounded-full mb-3 md:mb-4" data-id="element-670">
            {about.sectionLabel}
          </span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-earth mb-3 md:mb-4" data-id="element-671">
            {about.sectionTitle}
          </h2>
          <p className="text-stone text-base md:text-lg max-w-2xl mx-auto px-2" data-id="element-672">
            {about.sectionSubtitle}
          </p>
        </Reveal>

        {/* Sección de Franchelis - Fundadora */}
        <Reveal delay={100} className="mb-16 md:mb-20" data-id="element-673">
          <div className="bg-gradient-to-br from-sage/5 to-sand rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-12 border border-sage/10" data-id="element-674">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-675">
              <div className="space-y-5 md:space-y-6 order-2 lg:order-1" data-id="element-676">
                <div data-id="element-677">
                  <h3 className="font-display text-2xl sm:text-3xl md:text-4xl text-earth mb-1 md:mb-2" data-id="element-678">
                    {about.founderName}
                  </h3>
                  <p className="text-sage font-semibold text-base md:text-lg" data-id="element-679">
                    {about.founderRole}
                  </p>
                </div>

                <div className="space-y-3 md:space-y-4 text-stone text-sm md:text-base leading-relaxed" data-id="element-680">
                  {about.bio.map((paragraph, i) => <p key={i} data-id="element-681">{paragraph}</p>)}
                  <p className="text-earth font-medium italic" data-id="element-682">{about.quote}</p>
                </div>

                <div className="flex flex-wrap gap-2 md:gap-3 pt-2 md:pt-4" data-id="element-683">
                  {about.badges.map((badge, i) => <div key={i} className="px-3 md:px-4 py-1.5 md:py-2 bg-white rounded-full text-xs md:text-sm font-medium text-earth border border-sage/20 flex items-center gap-1.5" data-id="element-684">
                      {i === 0 ? <Award className="w-3.5 h-3.5 md:w-4 md:h-4 text-sage" data-id="element-685" /> : i === 1 ? <GraduationCap className="w-3.5 h-3.5 md:w-4 md:h-4 text-sage" data-id="element-686" /> : <Heart className="w-3.5 h-3.5 md:w-4 md:h-4 text-sage" data-id="element-687" />}
                      {badge}
                    </div>)}
                </div>
              </div>

              <Reveal delay={200} className="order-1 lg:order-2" data-id="element-688">
                <div className="rounded-2xl md:rounded-3xl overflow-hidden shadow-xl h-[350px] sm:h-[400px] md:h-[600px]" data-id="element-689">
                  <img src={about.founderImage} alt={`${about.founderName} practicando yoga`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" data-id="element-690" />
                </div>
              </Reveal>
            </div>
          </div>
        </Reveal>

        {/* Historia de Vimutti */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center mb-12 md:mb-16" data-id="element-691">
          <Reveal delay={100} data-id="element-692">
            <div className="space-y-4 md:space-y-6" data-id="element-693">
              <h3 className="font-display text-2xl md:text-3xl text-earth mb-2 md:mb-4" data-id="element-694">
                {about.historyTitle}
              </h3>
              {about.historyParagraphs.map((paragraph, i) => <p key={i} className="text-stone text-sm md:text-base leading-relaxed" data-id="element-695">
                  {paragraph}
                </p>)}
            </div>
          </Reveal>

          <Reveal delay={200} data-id="element-696">
            <div className="bg-sand rounded-2xl md:rounded-3xl p-6 md:p-8 space-y-5 md:space-y-6" data-id="element-697">
              <h3 className="font-display text-xl md:text-2xl text-earth mb-4 md:mb-6" data-id="element-698">
                {about.whyTitle}
              </h3>

              <div className="space-y-4" data-id="element-699">
                {about.whyItems.map((item, i) => <div key={i} className="flex items-start gap-3 md:gap-4" data-id="element-700">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-sage/20 rounded-xl flex items-center justify-center shrink-0" data-id="element-701">
                      {i === 0 ? <User className="w-5 h-5 md:w-6 md:h-6 text-sage" data-id="element-702" /> : i === 1 ? <Leaf className="w-5 h-5 md:w-6 md:h-6 text-sage" data-id="element-703" /> : i === 2 ? <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-sage" data-id="element-704" /> : <Heart className="w-5 h-5 md:w-6 md:h-6 text-sage" data-id="element-705" />}
                    </div>
                    <div data-id="element-706">
                      <h4 className="font-semibold text-earth text-sm md:text-base mb-0.5 md:mb-1" data-id="element-707">
                        {item.title}
                      </h4>
                      <p className="text-xs md:text-sm text-stone" data-id="element-708">
                        {item.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={300} data-id="element-709">
          <div className="bg-earth text-cream rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-12 text-center" data-id="element-710">
            <h3 className="font-display text-2xl md:text-3xl mb-3 md:mb-4" data-id="element-711">
              {about.missionTitle}
            </h3>
            <p className="text-cream/80 text-sm md:text-lg max-w-3xl mx-auto leading-relaxed" data-id="element-712">
              {about.missionText}
            </p>
          </div>
        </Reveal>
      </div>
    </section>;
}